<!-- BaseFile.vue (common) -->
<template tag="공통 파일 컴포넌트">
  <!-- 등록/수정 화면 -->
  <template v-if="viewType !== 'DETAIL'">
    <div class="infomation_box">
      <VFileUpload
        ref="fileInput"
        v-model="model"
        :multiple="fileConf.allowCnt > 1"
        :accept="allowedFileTypes[fileConf.allowType]"
        density="compact"
        variant="compact"
        clearable
        show-size
        @update:model-value="!props.upload ? changeFileUpload($event) : null"
        :title="props.title ?? $text('file.msg')"
      >
      </VFileUpload>
      <div>
        <!-- 업로드 화면일 경우 파일 크기 체크 메시지 미표시 -->
        <span class="mt-2 ml-12">
          <template v-if="!props.upload">
            * {{ $text("file.impMsg1") }} {{ fileConf.allowSize }} {{ $text("file.impMsg2") }}
            <br />
            * {{ $text("file.maxMsg", [fileConf.allowCnt]) }}
            <br />
          </template>
          * {{ $text("file.impMsg3") }} :
          {{ allowedFileTypes[props.fileConf.allowType] }}
        </span>
      </div>
    </div>
  </template>
  <!-- 상세 화면 -->
  <template v-else>
    <template v-if="fileInfo.length > 0">
      <template v-for="(file, idx) in fileInfo" :key="idx">
        <p>
          <v-icon
            v-if="data === 'admin'"
            :style="{ color: getFileIconInfo(file.ext).color, fontSize: '30px' }"
            class="mb-2"
          >
            {{ getFileIconInfo(file.ext).icon }}
          </v-icon>
          <a
            v-if="!props.noInfo"
            style="color: black"
            href="javascript:void(0)"
            @click="fnGetFileDown(file)"
          >
            {{ file.originalName + "." + file.ext }}
          </a>
          <template v-if="!props.noInfo">({{ bytesToMb(file.volume) }} Mb)</template>
        </p>
        <template v-if="fileConf.preView && fileConf.allowType === 'image'">
          <!-- <div class="img_wrap"> -->
          <img
            :src="previewSrc[idx]"
            style="max-width: 300px; margin-right: 8px"
            height="250"
            alt=""
          />
          <!-- </div> -->
        </template>
      </template>
      <!--      <v-btn @click="fnDownloadFileZip">zip</v-btn>-->
    </template>
    <template v-else>
      <p>{{ $text("file.noFile") }}</p>
    </template>
  </template>
</template>

<script setup lang="ts">
import { global, i18n } from "@/app/plugins/global";
import { onMounted, onScopeDispose, ref, watch } from "vue";
import { VFileUpload } from "vuetify/labs/VFileUpload";

import type { FileConf, FileInfoTypes } from "@/types/FileTypes";

import { downloadFile, getFileInfo, getFileList } from "@/api/cmmn/FileApi";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const vm = global();
const { $text } = i18n();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
const model = defineModel<File[]>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
/** 허용 파일 설정 */
const props = defineProps<{
  /**
   * @param fileConf
   * @description file tag 설정값
   * allowType - 허용 확장자 구분 파라미터
   * allowSize  - 개별 파일 최대 용량제한(mb)
   * allowCnt   - 허용 갯수
   * preView    - 미리보기 여부
   * preViewSrc - 미리보기 이미지 경로
   * noInfo - 정보 미표시 여부
   */
  fileConf: FileConf;
  fileGroupId?: string;
  title: string;
  viewType: string;
  upload?: boolean;
  noInfo?: boolean;
}>();

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
/** env */
const data = import.meta.env.VITE_APP_TYPE;
const fileInfo = ref<FileInfoTypes[]>([]);
const previewSrc = ref<string[]>([]); // 서버에서 받은 URL 저장

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 파일 첨부 */
const changeFileUpload = (files: File | File[] | null) => {
  if (props.fileConf.preView) {
    previewSrc.value = [];
  }

  // files가 null/undefined이면 종료
  if (!files) return;

  // files가 배열이 아니면 배열로 변환
  const fileList = Array.isArray(files) ? files : [files];

  // 입력 파일 갯수 확인
  if (fileList.length > props.fileConf.allowCnt) {
    vm.$alert($text("file.maxMsg", [props.fileConf.allowCnt]));
    model.value = [];
    return;
  }

  // 입력 파일 확장자 및 용량 확인
  const allowFiles = fileList.filter(fnAllowCheck);
  model.value = allowFiles;

  if (props.fileConf.preView) {
    previewSrc.value = allowFiles.map((file) => URL.createObjectURL(file));
  }
};

/** fileConf의 allowType에 따른 허용 확장자 */
const allowedFileTypes: Record<string, string[]> = {
  office: [".hwp", ".doc", ".docx", ".pdf", ".txt", ".ppt", ".pptx", ".xls", ".xlsx"],
  image: [".jpeg", ".jpg", ".png", ".gif"],
  excel: [".xlsx", ".csv"],
  custom: props.fileConf.allowTypes || [],
};

/** 파일 허용 확장자, 파일 용량 확인 메서드 */
const fnAllowCheck = (file: File): boolean => {
  // input된 file의 확장자 (추후 mimetype으로 체크 변경해야 할 수 있음.)
  const fileType = file.name.substring(file.name.lastIndexOf("."));

  const allowFileType = allowedFileTypes[props.fileConf.allowType] || [];
  const isTypeAllowed = allowFileType.includes(fileType);
  const isSizeAllowed = file.size <= props.fileConf.allowSize * 1e6; // n MB 이하
  if (!isTypeAllowed) {
    vm.$alert(`${allowFileType.join(", ")} <br> ${$text("file.impMsg4")}`);
    return false;
  }
  if (!isSizeAllowed && !props.upload) {
    vm.$alert(`${props.fileConf.allowSize} ${$text("file.impMsg2")}`);
    return false;
  }
  return true;
};

/** 파일 리스트 조회 */
const fnGetFileList = async () => {
  const param = {
    groupId: props.fileGroupId,
  };
  const res = await getFileList(param);
  if (res) {
    fileInfo.value = res;
    await fnSetFileByFileInfo();
  }
};

/** 파일 정보 조회 */
const fnSetFileByFileInfo = async () => {
  const filesPromises = fileInfo.value.map((file) =>
    getFile(file.link + file.saveName, file.originalName + "." + file.ext)
  );
  const files = await Promise.all(filesPromises);
  model.value = files.filter((file) => file) as File[];
};

/** 저장된 파일 정보 조회 */
const getFile = async (_url: string, _fileNm: string): Promise<File | null> => {
  try {
    const fileBlob = await getFileInfo(_url);
    return new File([fileBlob], _fileNm, { type: fileBlob.type });
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** download */
const fnGetFileDown = async (file: FileInfoTypes) => {
  try {
    const response = (await downloadFile(file.link + file.saveName)) as unknown as {
      data: Blob;
      headers: { "content-type": string };
    };

    // Blob 생성
    const blob = new Blob([response.data], { type: response.headers["content-type"] });

    // 가짜 링크 생성 후 클릭 이벤트 실행
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${file.originalName}.${file.ext}`; // 원하는 파일명으로 설정
    document.body.appendChild(link);
    link.click();

    // 메모리 정리
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  } catch (error) {
    console.error($text("file.downloadFail"), error);
  }
};
// const fnDownloadFileZip = async () => {
//   if (props.fileGroupId) {
//     try {
//       const res = await downloadFileZip(props.fileGroupId);
//
//       const fileName = "files.zip";
//
//       // 브라우저에서 다운로드 트리거
//       const url = window.URL.createObjectURL(
//         new Blob([res.data as string], { type: "application/zip" })
//       );
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute("download", fileName);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       console.error($text("file.downloadFail"), error);
//     }
//   }
// };

/** 파일 리셋 */
const fnGetFileReset = () => {
  model.value = [];
  fileInfo.value = [];
};

/** bytesToMb */
const bytesToMb = (bytes: number) => {
  return (((bytes / (1024 * 1024)) * 100) / 100).toFixed(3);
};

/** 확장자 명에 따른 mdi Icon / color */
const getFileIconInfo = (ext = "") => {
  switch (ext.toLowerCase()) {
    case "pdf":
      return { icon: "mdi-file-powerpoint", color: "lightcoral" };
    case "doc":
    case "docx":
      return { icon: "mdi-file-word", color: "#2a5699" };
    case "xls":
    case "xlsx":
      return { icon: "mdi-file-excel", color: "#21a366" };
    case "ppt":
    case "pptx":
      return { icon: "mdi-file-powerpoint", color: "#d24726" };
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return { icon: "mdi-file-image", color: "#888" };
    case "zip":
    case "rar":
      return { icon: "mdi-folder-zip", color: "#fce57c" };
    case "txt":
      return { icon: "mdi-file-document-outline", color: "#888" };
    default:
      return { icon: "mdi-file", color: "#888" };
  }
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
watch(
  () => props.fileGroupId,
  (newVal) => {
    if (newVal) {
      fnGetFileList();
    } else if (newVal === "") {
      fnGetFileReset();
    }
  }
);

watch(model, (newVal) => {
  if (newVal && newVal.length > 0 && props.fileConf.preView) {
    previewSrc.value = newVal.map((file) => URL.createObjectURL(file));
  }

  // 파일이 모두 삭제되거나 초기화되면 미리보기도 초기화
  if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
    // fnGetFileReset();
    // 이전 createObjectURL로 만든 URL 해제
    if (props.fileConf.preView && previewSrc.value.length > 0) {
      previewSrc.value.forEach((src) => URL.revokeObjectURL(src));
      previewSrc.value = [];
    }
  }
});

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
onMounted(() => {
  if (props.fileGroupId) {
    fnGetFileList();
  }
});

onScopeDispose(() => {
  // 컴포넌트가 언마운트될 때 createObjectURL로 만든 URL 해제
  if (props.fileConf.preView && previewSrc.value.length > 0) {
    previewSrc.value.forEach((src) => URL.revokeObjectURL(src));
  }
});

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
defineExpose({
  fnGetFileReset,
});
</script>
<style scoped>
:deep(.v-file-upload.v-file-upload--density-compact) {
  padding: 0 !important;
}
</style>
